'use client'
import { type FC, useState } from 'react'
import { Flex, Heading, HStack, Icon, Stack, Text, VStack } from '@chakra-ui/react'
import { EmptyMessage, GrandLink, GrandSection } from 'ui'
import fetchJson, { FetchError } from 'api/utils/next/fetchJson'
import type { OverridesContextType } from 'ui/lib/overrides'
import { OverridesContextProvider, overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { SignupForm, type SignupFormType } from 'forms'
import { AiFillCheckCircle } from 'react-icons/ai'
import { useUser } from '../../index'

interface SignupPageStdConfig {
  paths: {
    signupApiRoute: string
  }
  redirects: {
    onLoggedIn: string
    toNewPassword: string
    toLogin: string
  }
  overrides?: OverridesContextType
}

export interface SignupPageStdOverrides {
  registerAccount?: string
  signupPageSubtitle?: string
  signupAlreadyHaveAccount?: string
  toLogin?: string
  signUpSuccessMsg?: string
  signUpErrorMsgRecoverPassword?: string
  signupRecoverPasswordLink?: string
  signupInvalidCredentials?: string
}

interface SignupPageStdProps {
  config: SignupPageStdConfig
}

const SignupPageStd: FC<SignupPageStdProps> = ({ config }) => {
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const overrides = useOverridesContext<keyof SignupPageStdOverrides>()

  useUser({
    redirectTo: config.redirects.onLoggedIn,
    redirectIfFound: true
  })

  const title = overrideText('Skapa konto', overrides?.registerAccount)
  const subtitle = overrideText(
    'Registrera dig för att kunna följa beställningar och hantera dina uppgifter.',
    overrides?.signupPageSubtitle
  )
  const alreadyLabel = overrideText('Har du redan ett konto?', overrides?.signupAlreadyHaveAccount)
  const toLoginLabel = overrideText('Logga in', overrides?.toLogin)
  const recoverPasswordPrefix = overrideText(
    'Din e-postadress finns redan registrerad hos oss. Har du glömt ditt lösenord? Klicka',
    overrides?.signUpErrorMsgRecoverPassword
  )
  const recoverPasswordLink = overrideText('här', overrides?.signupRecoverPasswordLink)

  const handleSignup = async (data: SignupFormType) => {
    try {
      await fetchJson(config.paths.signupApiRoute, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, type: 'CONSUMER' })
      })
      setSuccessMsg(
        overrideText(
          'Kontot har registrerats — du får snart ett mail med instruktioner.',
          overrides?.signUpSuccessMsg
        )
      )
      setErrorMsg('')
    } catch (error) {
      if (error instanceof FetchError) {
        setErrorMsg(
          error.data?.message ||
            overrideText('Ogiltiga uppgifter', overrides?.signupInvalidCredentials)
        )
        setSuccessMsg('')
      } else {
        console.error('An unexpected error happened:', error)
      }
    }
  }

  return (
    <OverridesContextProvider overrides={{ ...config.overrides }}>
      <GrandSection sectionId="signup">
        <Flex justify="center" w="full">
          <VStack
            align="stretch"
            bg="background.default"
            borderColor="gray.100"
            borderRadius="md"
            borderWidth={1}
            maxW="md"
            p={{ base: 6, md: 8 }}
            spacing={6}
            w="full"
          >
            <VStack align="stretch" spacing={2}>
              <Heading as="h1" fontSize={{ base: '2xl', md: '3xl' }}>
                {title}
              </Heading>
              <Text color="text.mild" fontSize="sm">
                {subtitle}
              </Text>
            </VStack>

            {successMsg ? (
              <VStack p={4} spacing={6} w="full">
                <Icon as={AiFillCheckCircle} color="status.success" fontSize="6xl" />
                <EmptyMessage text={successMsg} textAlign="left" />
              </VStack>
            ) : (
              <>
                <SignupForm error={errorMsg} onSubmit={handleSignup} />
                {errorMsg ? (
                  <Text color="text.mild" fontSize="sm">
                    {recoverPasswordPrefix}{' '}
                    <GrandLink color="primary" href={config.redirects.toNewPassword}>
                      {recoverPasswordLink}
                    </GrandLink>
                  </Text>
                ) : null}
              </>
            )}

            <Stack
              align={{ base: 'stretch', sm: 'center' }}
              direction={{ base: 'column', sm: 'row' }}
              fontSize="sm"
              justify="space-between"
              spacing={2}
            >
              <HStack spacing={1}>
                <Text color="text.mild">{alreadyLabel}</Text>
                <GrandLink color="primary" fontWeight="semibold" href={config.redirects.toLogin}>
                  {toLoginLabel}
                </GrandLink>
              </HStack>
            </Stack>
          </VStack>
        </Flex>
      </GrandSection>
    </OverridesContextProvider>
  )
}

export default SignupPageStd
